import React, { useContext, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { Info } from "lucide-react";
import HealthDetailDrawer from "../Components/HealthDetailDrawer";
import { GlobalContext } from "../Context/GlobalContext";
import { images } from "../Assets/images";
import Chart from "react-apexcharts";
import { normalizeScanDetails } from "./scanDetails/scanNormalizer";
import {
  styleKeyFromCategory,
  statusIconMap,
  getCategoryStyle,
  shortLabels,
  categoryHeaders,
} from "../utils";

const normalizeKey = (str) => str?.toLowerCase().replace(/\s+/g, "_") || "";

const cardOrder = ["heart_health", "blood", "stress", "body", "risks"];

const ShowAllResult = () => {
  const {
    scanDetails,
    translatedLabels,
    getScanDetails,
    wellnessLabel,
    is4Life,
  } = useContext(GlobalContext);
  const location = useLocation();
  const scanId = location.state?.scanId;
  const screenLabels = translatedLabels?.common;

  const { state } = useLocation();
  const initialFilter = state?.selectedFilter || "all";
  const initialFilterKey = normalizeKey(initialFilter);

  const [showCardDetail, setShowCardDetail] = useState(false);
  const [activeTab, setActiveTab] = useState(initialFilterKey);
  const [selectedMetricData, setSelectedMetricData] = useState(null);
  const [activePill, setActivePill] = useState(initialFilterKey);

  const normalizedScanDetailss = useMemo(
    () => normalizeScanDetails(scanDetails, screenLabels),
    [scanDetails, screenLabels],
  );

  useEffect(() => {
    getScanDetails(scanId || "");
  }, [getScanDetails]);

  useEffect(() => {
    if (showCardDetail) {
      window.history.pushState({ drawerOpen: true }, "");
      const handlePopState = () => {
        if (showCardDetail) setShowCardDetail(false);
      };
      window.addEventListener("popstate", handlePopState);
      return () => {
        window.removeEventListener("popstate", handlePopState);
        if (window.history.state?.drawerOpen) window.history.back();
      };
    }
  }, [showCardDetail]);

  const allMetricCards = useMemo(() => {
    if (!normalizedScanDetailss || normalizedScanDetailss.length === 0)
      return [];

    let cards = [];
    normalizedScanDetailss.forEach((categoryData) => {
      const categoryName = categoryData.category;
      const categoryKey = normalizeKey(categoryName);
      if (categoryKey === "wellness") return;

      categoryData.metrics.forEach((metric) => {
        if (!metric.value) return;
        cards.push({
          id: `${categoryName}-${metric.key}`,
          key: categoryName,
          categoryKey,
          title: metric.label || metric.key,
          category: metric.label || metric.key,
          categoryScore: metric.value,
          avgReadingScore: metric.avg || "-",
          rawMetric: {
            key: metric.key,
            value: metric.value,
            cronologicalAge: metric.cronologicalAge,
            unit: metric.unit || "",
            avg: metric.avg || "",
            comment: metric.label || metric.key,
            status: metric.status || "Normal",
            normal: metric.normal || "N/A",
          },
        });
      });
    });

    if (
      scanDetails?.stress?.rri_data &&
      scanDetails.stress.rri_data !== "Array"
    ) {
      cards.push({
        id: "rri_data_chart",
        key: "Stress",
        categoryKey: "stress",
        title: screenLabels?.rri_data_chart?.label_value || "RRI Data Chart",
        category: "Heart Rate Variability",
        categoryScore: "Chart Data",
        avgReadingScore: "-",
        rawMetric: {
          key: "rri_data",
          value: scanDetails.stress.rri_data,
          unit: "ms",
          status: "Info",
          comment:
            screenLabels?.rri_data_chart?.label_value || "RRI Data Chart",
        },
        isRRIChart: true,
      });
    }

    return cards;
  }, [normalizedScanDetailss, screenLabels, scanDetails]);

  const metricCards = useMemo(() => {
    if (activePill === "all") return allMetricCards;
    return allMetricCards.filter((c) => c.categoryKey === activePill);
  }, [allMetricCards, activePill]);

  const groupedCards = useMemo(() => {
    const grouped = {};
    metricCards.forEach((card) => {
      const key = card.categoryKey;
      if (!grouped[key]) grouped[key] = [];
      grouped[key].push(card);
    });
    return grouped;
  }, [metricCards]);

  const sortedGroups = Object.entries(groupedCards).sort(([aKey], [bKey]) => {
    const indexA = cardOrder.indexOf(aKey);
    const indexB = cardOrder.indexOf(bKey);
    return (indexA === -1 ? 999 : indexA) - (indexB === -1 ? 999 : indexB);
  });

  const pillCategories = useMemo(() => {
    const headers = categoryHeaders(screenLabels);
    return cardOrder
      .filter((key) => allMetricCards.some((c) => c.categoryKey === key))
      .map((key) => ({ key, ...headers[key] }))
      .filter(Boolean);
  }, [allMetricCards, screenLabels]);

  const renderCard = (card) => {
    const { icon, color } = getCategoryStyle(card.key);

    if (card.isRRIChart) {
      return (
        <div
          key={card.id}
          className={`card ${
            is4Life ? `fourlife-shadow-stress` : `shadow-stress`
          } d-flex flex-column px-2 py-2 mb-4`}
          style={{ cursor: "pointer" }}
          onClick={() => {
            setSelectedMetricData({
              key: card.rawMetric.key,
              value: card.rawMetric.value,
              unit: "ms",
              status: "Info",
              comment: card.rawMetric.comment,
              source: is4Life ? "show_all_result" : "",
            });
            setShowCardDetail(true);
          }}
        >
          <div className="scan-result-header d-flex justify-content-between align-items-center">
            <div className="d-flex gap-2">
              <div>
                <h6 className="mt-auto mb-0">{card.title}</h6>
                <p className="mb-0 fs-13px">
                  {screenLabels?.rr_intervals?.label_value ||
                    "Heart rate trend from R-R intervals"}
                </p>
              </div>
            </div>
          </div>
          <div className="mt-2">
            <Chart
              type="line"
              height={180}
              options={{
                chart: {
                  id: "rri-heart-rate-chart",
                  toolbar: { show: false },
                  zoom: { enabled: false },
                },
                stroke: { curve: "smooth", width: 2 },
                markers: { size: 0 },
                xaxis: {
                  title: {
                    text: screenLabels?.time_s?.label_value || "Time (s)",
                  },
                  labels: { formatter: (val) => Number(val).toFixed(1) },
                },
                yaxis: {
                  title: {
                    text:
                      screenLabels?.heart_rate_bpm?.label_value ||
                      "Heart Rate (BPM)",
                  },
                  min: 40,
                  max: 140,
                },
                tooltip: {
                  x: { formatter: (val) => `${Number(val).toFixed(2)}s` },
                  y: { formatter: (val) => `${Number(val).toFixed(2)} BPM` },
                },
              }}
              series={[
                {
                  name: screenLabels?.heart_rate?.label_value || "Heart Rate",
                  data: Array.isArray(card.rawMetric.value)
                    ? card.rawMetric.value
                        .map((entry) =>
                          entry &&
                          typeof entry === "object" &&
                          "timestamp" in entry &&
                          "interval" in entry
                            ? [
                                Number(entry.timestamp),
                                Math.round((60000 / entry.interval) * 100) /
                                  100,
                              ]
                            : null,
                        )
                        .filter(Boolean)
                    : [],
                },
              ]}
            />
          </div>
          <div className="info-icon">
            <Info size={18} />
          </div>
        </div>
      );
    }

    const isHeartAge = card.rawMetric?.key === "heart_age";
    return (
      <div
        key={card.id}
        className={`card ${
          is4Life
            ? `fourlife-shadow-${styleKeyFromCategory(card.key)}`
            : `shadow-${styleKeyFromCategory(card.key)}`
        } d-flex flex-column px-2 py-2 mb-3`}
        style={{ cursor: "pointer" }}
        onClick={() => {
          setActiveTab(card.categoryKey);
          setSelectedMetricData({
            ...card.rawMetric,
            source: is4Life ? "show_all_result" : "",
            icon: statusIconMap[card.rawMetric.status] || images.normalIcon,
          });
          setShowCardDetail(true);
        }}
      >
        <div className="d-flex flex-row justify-content-between w-100">
          <div className={`icon-wrapper ${color}`}>
            <img
              src={icon}
              alt={card.category}
              style={{ width: "24px", height: "24px" }}
            />
          </div>
          <div className="flex-grow-1 px-2">
            <h6 className="mb-0 fw-semibold welless-card-title">
              {card.title}
            </h6>
            <div className="d-flex justify-content-between bp-details">
              <div>
                <p className="mb-0 font-bold fs-14px">
                  {isHeartAge
                    ? screenLabels?.cronological_age?.label_value ||
                      "Chronological Age"
                    : screenLabels?.reading?.label_value || "Current Reading"}
                </p>
                <p className="mb-0 fs-14px fw-semibold">
                  {isHeartAge
                    ? card.rawMetric.cronologicalAge || "-"
                    : card.categoryScore}
                </p>
              </div>
              <div>
                <p className="mb-0 fs-14px text-end">
                  {isHeartAge
                    ? screenLabels?.biological_age?.label_value ||
                      "Biological Age"
                    : wellnessLabel || "-"}
                </p>
                <p className="mb-0 fs-14px fw-semibold">
                  {isHeartAge
                    ? card.categoryScore
                    : card.avgReadingScore || "-"}
                </p>
              </div>
            </div>
          </div>
          <div className="info-icon me-1">
            <Info size={18} />
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="content-part pt-5">
      <div
        className="d-flex gap-2 align-items-center flex-nowrap overflow-auto py-3 mb-2"
        style={{
          position: "sticky",
          top: "0px",
          zIndex: 10,
          background: "var(--pageBgColor, #fff)",
          boxShadow: "0 2px 6px rgba(0,0,0,0.04)",
          scrollbarWidth: "none",
          msOverflowStyle: "none",
        }}
      >
        <button
          onClick={() => setActivePill("all")}
          className={`btn btn-sm rounded-pill px-3 py-1 ${
            activePill === "all" ? "btn-dark" : "btn-outline-secondary"
          }`}
          style={{ whiteSpace: "nowrap", fontSize: "13px" }}
        >
          {screenLabels?.all?.label_value || "All"}
        </button>

        {pillCategories.map(({ key, icon, name }) => (
          <button
            key={key}
            onClick={() => setActivePill(key)}
            className={`btn btn-sm rounded-pill px-3 d-flex align-items-center gap-1 ${
              activePill === key
                ? getCategoryStyle(key).color
                : "btn-outline-secondary"
            }`}
            style={{ whiteSpace: "nowrap", fontSize: "13px" }}
          >
            {icon && (
              <img
                src={icon}
                alt={name}
                style={{ width: "14px", height: "14px" }}
              />
            )}
            {name}
          </button>
        ))}
      </div>
      <div className="">
        {sortedGroups.map(([, categoryCards]) => categoryCards.map(renderCard))}
      </div>

      <HealthDetailDrawer
        show={showCardDetail}
        onClose={() => setShowCardDetail(false)}
        activeTab={activeTab}
        showIconAndImage={true}
        metricData={selectedMetricData}
      />
    </div>
  );
};

export default ShowAllResult;
